import { Injectable, OnDestroy } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { Subscription } from "rxjs";
import { DrawerComponent } from "../layout/drawer/index.component";

export type DrawerConfig = {
  title: string;
  subTitle?: string;
  mode?: "outer" | "inner";
  top?: number;
};

export const DefaultDrawerConfig: Readonly<DrawerConfig> = {
  title: "Title",
  mode: "inner",
};

export type DrawerShowInstance<T> = {
  instance: T;
  result: Promise<{ type: "complete" | "hide"; data?: any }>;
};

export type DrawerChangeEvent = {
  type: "show" | "hide" | "complete";
  cancel: () => void;
  detail: Record<string, any>;
};

export type DrawerChangeListener = (event: DrawerChangeEvent) => void;

@Injectable({
  providedIn: "root",
})
export class DrawerService implements OnDestroy {
  private uniqueDrawerComponent: DrawerComponent;
  private resultPromiseResolveMap = new Map<string, ((value: any) => void)[]>();

  private routerSubscription: Subscription;
  private ListenChangeCbs: Set<DrawerChangeListener> = new Set();

  constructor(private router: Router) {}

  get cacheKey() {
    return this.router.url;
  }

  ngOnDestroy() {
    this.removeDrawer();
  }

  listenRouterChange() {
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const drawer = this.uniqueDrawerComponent;
        drawer && drawer.onRouteChange();
      });
  }

  setDrawer(component: DrawerComponent) {
    if (this.uniqueDrawerComponent) {
      this.removeDrawer();
    }
    this.uniqueDrawerComponent = component;
    this.listenRouterChange();
  }

  removeDrawer() {
    this.routerSubscription && this.routerSubscription.unsubscribe();
    this.routerSubscription = null;
    this.uniqueDrawerComponent = null;
  }

  show<T>(
    component: new (...args: any[]) => T,
    config: DrawerConfig,
    data = {},
  ): DrawerShowInstance<T> {
    const drawer = this.uniqueDrawerComponent;
    if (!this.checkChange("show", { component, config, data }) || !drawer) {
      return {
        instance: null,
        result: null,
      };
    }
    return {
      instance: drawer.show(component, config, data),
      result: new Promise((resolve) => {
        const cacheKey = this.cacheKey;
        const resolves = this.resultPromiseResolveMap.get(cacheKey) || [];

        const resolveFn = (value: any) => {
          resolve(value);

          const caches = this.resultPromiseResolveMap.get(cacheKey) || [];
          const index = caches.findIndex((cache) => cache === resolveFn);
          caches.splice(index, 1);
          if (index === 0) {
            this.resultPromiseResolveMap.delete(cacheKey);
          }
        };

        this.resultPromiseResolveMap.set(cacheKey, [...resolves, resolveFn]);
      }),
    };
  }

  hide() {
    const drawer = this.uniqueDrawerComponent;
    const resolves = this.resultPromiseResolveMap.get(this.cacheKey) || [];
    const resolve = resolves.slice(-1)[0];
    if (resolve && this.checkChange("hide")) {
      resolve({ type: "hide" });
      drawer && drawer.back();
    }
  }

  complete(data?: any) {
    const drawer = this.uniqueDrawerComponent;
    const resolves = this.resultPromiseResolveMap.get(this.cacheKey) || [];
    const resolve = resolves.slice(-1)[0];
    if (resolve && this.checkChange("complete", { data })) {
      resolve({ type: "complete", data });
      drawer && drawer.back();
    }
  }

  setConfig(config: Partial<DrawerConfig>) {
    if (this.uniqueDrawerComponent) {
      this.uniqueDrawerComponent.setConfig(config);
    }
  }

  deleteCache(cacheKey: string) {
    const drawer = this.uniqueDrawerComponent;
    drawer && drawer.deleteAllCache(cacheKey);
    this.resultPromiseResolveMap.delete(cacheKey);
  }

  onChange(callback: DrawerChangeListener) {
    this.ListenChangeCbs.add(callback);
    return () => {
      this.ListenChangeCbs.delete(callback);
    };
  }

  private checkChange(
    type: DrawerChangeEvent["type"],
    detail: Record<string, any> = {},
  ) {
    let cancel = false;
    const event = { type, cancel: () => (cancel = true), detail };
    this.ListenChangeCbs.forEach((cb) => cb(event));
    return !cancel;
  }
}
